import { Box, Grid, InputAdornment, Typography } from '@mui/material';
import { colors } from 'constants/theme';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/** API */
import DentistCostApi from 'api/finances/dentist-cost.api';
import BranchApi from 'api/setting/branch-service-points/branch.api';
import { routeName } from 'routes/routes-name';

/** UTILS || SLICE */
import { getBranch } from 'utils/app.utils';
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils';

/** STYLE */

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { swalActive } from 'component/Alert/Swal';
import HeaderCard from 'component/CardCustom/HeaderCard';
import InputSecondNewDatePicker, { PICKER_VIEWS } from 'component/Input/InputSecondNewDatePicker';
import TableRowExport from 'component/Pdf/TableRowExport';
import TableCustom from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import { notiError, notiSuccess } from 'component/notifications/notifications';
import moment from 'moment';
import DropdownButton from 'new-components/buttons/dropdown-button';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import { dateTimeToView, dateToView } from 'utils/date.utils';
import { exportSalaryToExcel } from './exportSalaryToExcel';

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function Salary() {
  const { t } = useTranslation()
  const componentRef = useRef<any>(null)
  const history = useHistory()
  const [useType] = useState('')
  const [exportData, setExportData] = useState<any>([])

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('documentDate')
  const [sortType, setSortType] = useState('DESC')
  const [search, setSearch] = useState<string>('')
  const status = ''
  const [monthly, setMonthly] = useState('')

  /** CALL API */
  const [branches, setBranches] = useState([])
  const [dentistCost, setDentistCost] = useState([])
  const [branchId, setBranchId] = useState<number>(Number(getBranch()))

  const loadData = async () => {
    try {
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
      if (search) condition = { ...condition, search: search }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (status) condition = { ...condition, status: status }
      if (branchId) condition = { ...condition, branchId: branchId }
      if (monthly) condition = { ...condition, monthly: monthly }

      const res = await DentistCostApi.findAll(condition)
      const resExport = await DentistCostApi.findAll({ ...condition, export: 1 })
      if (res.status === 200) {
        setRowCount(res.headers['x-total'])
        setDentistCost(res.data)
        setExportData(resExport.data)
      }
    } catch (error) {
      notiError(t('ERROR'))
    }
  }


  const loadBranch = async () => {
    let condition: any = { page: 1, pageLimit: 99 }
    if (useType) condition = { ...condition, page: useType }
    const res = await BranchApi.findAll(condition)
    if (res.status === 200) {
      setBranches(res.data)
    }
  }

  useEffect(() => {
    loadBranch()
  }, [])

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])


  const onCreate = () => {
    window.location.href = routeName.salary + '/create'
  }

  const onEdit = (data: any) => {
    const { dentistCostId } = data
    history.push(routeName.salary + '/update', { dentistCostId: dentistCostId })
  }

  const onView = (data: any) => {
    const { dentistCostId } = data
    history.push(routeName.salary + '/view', { dentistCostId: dentistCostId })
  }

  const onDelete = (data: any) => {
    const { dentistCostId, documentNo, prefix } = data

    swalActive(t('SALARY.ALERT.CONFIRM_DELETE'), t('SALARY.ALERT.DOCUMENT_NO') + ' ' + prefix + documentNo, (res: any) => {
      if (res) {
        DentistCostApi.remove(dentistCostId)
          .then((resp) => {
            notiSuccess(t('SALARY.MESSAGE.SUCCESS.DELETE'))
            loadData()
          })
          .catch((e) => {
            notiError(t('SALARY.MESSAGE.ERROR'))
          })
      }
    }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL'))
  }

  const handleChangePage = (val: number) => setPage(val)

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeMonthly = (valueMonthly: string) => {
    setMonthly(valueMonthly)
  }

  const headCells = [
    { id: 'dentistCostItemId,', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.ID'), align: 'center', width: '80px' },
    { id: 'documentNo', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.DOCUMENT_NO') },
    { id: 'monthly', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.MONTHLY') },
    { id: 'documentDate', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.DOCUMENT_CREATION_DATE'), width: '150px' },
    { id: 'price', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.TOTAL'), width: '150px' },
    { id: 'documentStatus', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.DOCUMENT_STATUS'), width: '180px' },
    { id: 'updatedBy', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.CREATED_BY'), width: '180px' },
    { id: 'action', disablePadding: false, label: t('SALARY.TABLE.CELL.HEADER.ACTION'), width: '150px' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { dentistCostItemId, documentNo, monthly: salaryMonthly, documentDate, total, documentStatus, updatedBy, updatedAt, prefix } = objData

    const documentNoId = prefix + documentNo

    const formatYmd = new Date(salaryMonthly)
    const resultMonthly = formatYmd.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long'
    })

    const formatDate = new Date(documentDate)
    const resultDocumentDate = formatDate.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })

    const numb = total
    const numbTotal = numb.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    let docStatusBgColor = ''
    let documentStatusbar = ''
    if (documentStatus === 'PENDING_APPROVAL') {
      docStatusBgColor = colors.approveStatus.pending
      documentStatusbar = t('SALARY.DOCUMENT_STATUS.PENDING_APPROVAL')
    } else if (documentStatus === 'APPROVAL') {
      docStatusBgColor = colors.approveStatus.approve
      documentStatusbar = t('SALARY.DOCUMENT_STATUS.APPROVAL')
    } else if (documentStatus === 'UNAPPROVAL') {
      docStatusBgColor = colors.approveStatus.cancel
      documentStatusbar = t('SALARY.DOCUMENT_STATUS.UNAPPROVAL')
    } else {
      docStatusBgColor = 'transparent'
    }
    const objRenderData = {
      key: dentistCostItemId,
      id: dentistCostItemId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'left', label: documentNoId },
        { option: 'TEXT', align: 'left', label: resultMonthly },
        { option: 'TEXT', align: 'left', label: resultDocumentDate },
        { option: 'TEXT', align: 'right', label: numbTotal },
        {
          option: 'COMPONENT', component: valueExportData ? (<>{documentStatusbar}</>) : (
            <Box display={'flex'} alignItems={'center'} gap={'12px'}>
              <Box sx={{ borderRadius: '50%', backgroundColor: docStatusBgColor, width: '15px', height: '15px' }} />
              <Typography> {documentStatusbar}</Typography>
            </Box>
          )
        },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          newIconAction: true,
          values: [
            { option: 'VIEW', label: t(`BUTTON.VIEW`) },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: documentStatus !== 'PENDING_APPROVAL' },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), }
          ]
        }
      ]
    }

    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onView={() => onView(objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => onDelete(objRenderData.obj)}
          />
        }
      </>
    )
  }
  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('DASHBOARD.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName') || '-'}` },
    {
      label: `${t('SALARY.INPUT.MONTH')} : ${new Date(monthly).toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long'
      }) || '-'}`
    },
  ]
  const resultPDFMonth = (month: any) => {
    const formatYmd = new Date(month)
    return formatYmd.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long'
    })
  }

  const resultDocumentDate = (date: any) => {
    const formatDate = new Date(date)
    return formatDate.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  return (
    <Box>
      <HeaderCard text={t('SALARY.TITLE')} />
      <Box paddingTop="16px" paddingX="24px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              fullWidth
              label={t('SALARY.INPUT.MAIN_SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('SALARY.INPUT.BRANCH')}
              value={branchId}
              onChange={(event: any) => {
                setBranchId(event.target.value)
                setPage(1)
              }}
              options={branches.map((i: any) => ({ value: i.branchId, name: i.branchName }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewDatePicker
              allowClear
              views={PICKER_VIEWS.MONTH}
              // views={[PICKER_VIEWS.YEAR, PICKER_VIEWS.MONTH]}
              onchange={(e: any) => {
                handleChangeMonthly(moment(e).format('YYYY-MM-DD'))
                loadData()
                setPage(1)
              }}
              onClear={() => setMonthly('')}
              value={monthly}
              dateFormat="MMMM YYYY"
              inputHeight={40}
              placeholder={t('SALARY.INPUT.MONTH')} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={1}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} xl={5}>
            <Box display={'flex'} justifyContent={'end'}>
              <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
                <DropdownButton title={'พิมพ์เอกสาร'} onChange={(e) => {
                  if (e === 'Excel') {
                    const report: any = {
                      title: t('SALARY.TITLE'),
                      filters: `${filter[1].label} ${filter[2].label}`,
                      rows: exportData.map((item: any) => ({
                        documentNo: item.prefix + item.documentNo,
                        monthly: new Date(item.monthly).toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'long'
                        }),        // "YYYY-MM-DD"
                        documentDate: dateToView(item.documentDate),   // ISO
                        total: item.total,
                        status: t(`SALARY.DOCUMENT_STATUS.${item.documentStatus}`),
                        updatedBy: item.updatedBy,
                        updatedAt: dateTimeToView(item.updatedAt)   // ISO
                      })),
                      t
                    }
                    exportSalaryToExcel(report)
                  } else if (e === 'PDF') {
                    const style = document.createElement("style");
                    style.innerHTML = `
                @media print {
                  @page {size: landscape }
                                }
                `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);

                  }
                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                <MainButton type='primary' variant='contained' disabled={permissions.isCreate.disabled} onClick={() => onCreate()} title={t('SALARY.BUTTON.ADD')} startIcon={<AddCircleIcon />} />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={dentistCost.map((val, i) => {
              return renderData(val, i, false)
            })}
            tableFixedWidth
            tableMinWidth={1336}
          />
        </Box>
      </Box>

      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t('SALARY.TITLE')}</Typography>
        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
          <Typography fontSize="10px" fontWeight={400}>{`${t('DASHBOARD.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName') || '-'}`}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{`${t('SALARY.INPUT.MONTH')} : ${monthly || '-'}`}</Typography>
        </Box>
        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('SALARY.TABLE.CELL.HEADER.DOCUMENT_NO')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('SALARY.TABLE.CELL.HEADER.MONTHLY')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('SALARY.TABLE.CELL.HEADER.DOCUMENT_CREATION_DATE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('SALARY.TABLE.CELL.HEADER.TOTAL')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('SALARY.TABLE.CELL.HEADER.DOCUMENT_STATUS')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('SALARY.TABLE.CELL.HEADER.CREATED_BY')}</th>
            </tr>
          </thead>
          <tbody>
            {exportData?.length === 0 &&
              <tr>
                <td colSpan={7} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
              </tr>
            }
            {exportData.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.prefix + data?.documentNo}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{resultPDFMonth(data?.monthly) || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{resultDocumentDate(data?.documentDate) || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center' }}>
                  {data?.total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center' }}>{data?.documentStatus === 'PENDING_APPROVAL' ? t('SALARY.DOCUMENT_STATUS.PENDING_APPROVAL') : data?.documentStatus === 'APPROVAL' ? t('SALARY.DOCUMENT_STATUS.APPROVAL') : t('SALARY.DOCUMENT_STATUS.UNAPPROVAL')}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <span>{data?.updatedBy || '-'}</span>
                  <span>{dateTimeToView(data?.updatedAt, false) || '-'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}


